import React, { useState, useEffect, useContext, useCallback } from 'react';
import { fmtDate as _fmtDate, humanizeDates } from '@/lib/dates';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import ServiceRequestTable from '@/components/wom/ServiceRequestTable';
import { SR_STATUS } from '@/components/wom/theme';
import { PRIORITY_SLA } from '@/components/wom/priority';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Inbox, AlertTriangle, ClipboardCheck, PauseCircle, HelpCircle, ChevronRight,
  ListChecks, Wrench, Clock3, CheckCircle2, CircleDot, BadgeDollarSign,
} from 'lucide-react';

const CLOSED = ['Cancelled', 'Closed by Admin', 'Closed', 'Completed', 'Work Completed'];
const COMPLETED = ['Closed by Admin', 'Closed', 'Completed', 'Work Completed'];
const reqDate = (w) => w.required_completion_date || w.required_service_date;
const isActive = (w) => !CLOSED.includes(w.status);
const isOverdue = (w) => {
  const d = reqDate(w);
  if (!d || !isActive(w)) return false;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return new Date(d) < t;
};
const isUnscheduled = (w) => isActive(w) && !(w.scheduled_date && String(w.scheduled_date).trim());
const isToday = (d) => {
  if (!d) return false;
  const a = new Date(d); const b = new Date();
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};
const fmtDate = (d) => (d ? (_fmtDate(d) || '—') : '—');

const SrMetric = ({ label, value, icon: Icon, tone = 'text-[#0E2A47]', onClick, testid }) => (
  <Card className={onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} onClick={onClick} data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className={`text-3xl font-extrabold ${tone}`}>{value ?? 0}</div>
          <div className="text-xs uppercase tracking-wide text-slate-400 mt-1">{label}</div>
        </div>
        <Icon size={24} className="text-slate-300" />
      </div>
    </CardContent>
  </Card>
);

const WoMetric = ({ label, value, tone = 'text-[#0E2A47]', onClick, testid, dimmed }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-lg border bg-white px-4 py-3 text-left transition-all hover:shadow-md hover:border-[#0E2A47]/20 ${dimmed ? 'opacity-70' : ''}`}>
    <div className={`text-2xl font-extrabold ${value ? tone : 'text-slate-300'}`}>{value ?? 0}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5 leading-tight">{label}</div>
  </button>
);

const OperationsDashboard = ({ onSelectRequest, onOpenRequests, onSelectWorkOrder, onOpenWorkOrders, pendingBilling = 0, isGA = false, onOpenBillingApprovals, followUpsDue = 0, onOpenFollowUps }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [stats, setStats] = useState({});
  const [newRequests, setNewRequests] = useState([]);
  const [wos, setWos] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, r, w] = await Promise.all([
        axios.get(`${API}/service-requests/stats/operations`, authHeaders),
        axios.get(`${API}/service-requests`, { ...authHeaders, params: { status_filter: SR_STATUS.SUBMITTED } }),
        axios.get(`${API}/work-orders`, authHeaders),
      ]);
      setStats(s.data || {});
      setNewRequests(r.data || []);
      setWos(w.data || []);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const SLA = Object.entries(PRIORITY_SLA).map(([p, t]) => ({ p, t }));

  // ---- Work Order derived metrics ----
  const countStatus = (st) => wos.filter((w) => w.status === st).length;
  const woMetrics = {
    open_unassigned: countStatus('Open - Unassigned'),
    assigned: countStatus('Assigned'),
    scheduled: countStatus('Scheduled'),
    dispatched: countStatus('Dispatched'),
    in_progress: countStatus('In Progress'),
    awaiting_authorization: countStatus('Awaiting Authorization'),
    on_hold: countStatus('On Hold'),
    awaiting_qc: countStatus('Awaiting Administrative Closeout'),
    overdue: wos.filter(isOverdue).length,
  };
  const p1Unassigned = wos.filter((w) => w.priority_level === 'P1' && (w.status === 'Open - Unassigned' || w.assignment_status !== 'Assigned') && isActive(w)).length;
  const p1Emergency = wos.filter((w) => w.priority_level === 'P1' && isActive(w) && (isUnscheduled(w) || isOverdue(w))).length;
  const otherOverdue = wos.filter((w) => isOverdue(w) && w.priority_level !== 'P1').length;
  const authRequired = countStatus('Awaiting Authorization');
  const awaitingQc = countStatus('Awaiting Administrative Closeout');

  const activeList = wos.filter(isActive).sort((a, b) => {
    // Newest first so freshly created/converted WOs always stay visible,
    // then surface overdue ahead of the rest for the same recency tier.
    const rc = new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (rc) return rc;
    return (isOverdue(b) ? 1 : 0) - (isOverdue(a) ? 1 : 0);
  });
  const completedList = wos.filter((w) => COMPLETED.includes(w.status))
    .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  const completedToday = completedList.filter((w) => isToday(w.updated_at)).length;

  // ---- Required Actions (clickable) ----
  const requiredActions = [
    ...(isGA && (pendingBilling || 0) > 0 ? [{
      key: 'billing-approval',
      label: `${pendingBilling} work order${pendingBilling === 1 ? '' : 's'} pending approval for billing`,
      count: pendingBilling, tone: 'text-emerald-700', hideCount: true, emphasis: true,
      onClick: () => onOpenBillingApprovals && onOpenBillingApprovals(),
    }] : []),
    ...((followUpsDue || 0) > 0 ? [{
      key: 'follow-ups-due',
      label: `${followUpsDue} follow-up${followUpsDue === 1 ? '' : 's'} due`,
      count: followUpsDue, tone: 'text-amber-700', hideCount: true,
      onClick: () => onOpenFollowUps && onOpenFollowUps(),
    }] : []),
    { key: 'approve-wo', label: 'New requests to review — client-authorized, ready for Work Order', count: stats.awaiting_wo_processing, tone: 'text-green-700', onClick: () => onOpenRequests(SR_STATUS.APPROVED) },
    { key: 'wo-unassigned', label: 'Work orders unassigned', count: woMetrics.open_unassigned, tone: 'text-amber-600', onClick: () => onOpenWorkOrders({ status: 'Open - Unassigned' }) },
    { key: 'wo-overdue-other', label: 'Other overdue work orders (P2–P5)', count: otherOverdue, tone: 'text-amber-600', onClick: () => onOpenWorkOrders({ special: 'overdue_other' }) },
    { key: 'auth-req', label: 'Authorization required', count: authRequired, tone: 'text-purple-600', onClick: () => onOpenWorkOrders({ status: 'Awaiting Authorization' }) },
    { key: 'awaiting-qc', label: 'Work completed awaiting administrative closeout', count: awaitingQc, tone: 'text-indigo-600', onClick: () => onOpenWorkOrders({ status: 'Awaiting Administrative Closeout' }) },
  ].filter((a) => (a.count || 0) > 0);
  const totalActions = requiredActions.reduce((n, a) => n + (a.count || 0), 0);

  const WO_CARDS = [
    { key: 'open_unassigned', label: 'Open – Unassigned', status: 'Open - Unassigned', tone: 'text-amber-600' },
    { key: 'assigned', label: 'Assigned', status: 'Assigned', tone: 'text-green-700' },
    { key: 'scheduled', label: 'Scheduled', status: 'Scheduled', tone: 'text-[#0E2A47]' },
    { key: 'dispatched', label: 'Dispatched', status: 'Dispatched', tone: 'text-[#0E2A47]' },
    { key: 'in_progress', label: 'In Progress', status: 'In Progress', tone: 'text-[#0E2A47]' },
    { key: 'awaiting_authorization', label: 'Awaiting Authorization', status: 'Awaiting Authorization', tone: 'text-purple-600' },
    { key: 'on_hold', label: 'On Hold', status: 'On Hold', tone: 'text-slate-600' },
    { key: 'awaiting_qc', label: 'Awaiting Closeout', status: 'Awaiting Administrative Closeout', tone: 'text-indigo-600' },
  ];

  return (
    <div className="space-y-8" data-testid="operations-dashboard">
      {/* Thin dashboard banner — supplied ROUT image, artwork kept fully visible on the
          right (object-contain right, no crop/stretch); live title on the white left side. */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="rout-dashboard-banner">
        <div className="relative h-[118px] sm:h-[148px]">
          <img src="/module-banners/rout-banner.webp" alt="" aria-hidden="true"
            className="absolute right-0 top-0 h-full w-auto max-w-none object-contain object-right" data-testid="rout-banner-img" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 30%, rgba(255,255,255,0.72) 46%, rgba(255,255,255,0) 66%)' }} aria-hidden="true" />
          <div className="absolute inset-0 sm:hidden" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 52%, rgba(255,255,255,0.82) 64%, rgba(255,255,255,0) 84%)' }} aria-hidden="true" />
          <div className="relative z-10 flex h-full flex-col justify-center px-6">
            <h1 className="text-[24px] sm:text-[32px] leading-tight font-extrabold tracking-tight text-[#0E2A47]" data-testid="rout-overview-title">Work Orders</h1>
          </div>
        </div>
      </div>

      {/* P1 Emergency — strongest visual attention (unscheduled OR overdue) */}
      {p1Emergency > 0 && (
        <button onClick={() => onOpenWorkOrders({ special: 'p1_emergency' })} data-testid="p1-emergency-alert"
          className="w-full flex items-center gap-4 rounded-xl border-2 border-red-500 bg-red-600 px-5 py-4 text-left text-white shadow-lg shadow-red-600/20 hover:bg-red-700 transition-colors">
          <AlertTriangle size={28} className="shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-extrabold uppercase tracking-wide">P1 Emergency — Unscheduled / Overdue</div>
            <div className="text-xs text-red-100 mt-0.5">High-priority work orders need immediate scheduling or are past due. Click to review now.</div>
          </div>
          <span className="rounded-md bg-white px-3 py-1 text-xl font-extrabold text-red-600">{p1Emergency}</span>
          <ChevronRight size={20} className="text-red-200" />
        </button>
      )}

      {/* Required Actions — conditional state (green when caught up, amber/orange when action required) */}
      {(() => {
        const allCaughtUp = requiredActions.length === 0;
        return (
          <div
            data-testid="required-actions-panel"
            data-state={allCaughtUp ? 'caught-up' : 'action-required'}
            className={`rounded-xl border p-5 transition-colors ${
              allCaughtUp ? 'border-[#5FA718]/40 bg-[#5FA718]/10' : 'border-[#E8C99B] bg-[#FBF3E4]'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className={`flex items-center gap-2 font-bold uppercase tracking-wide text-sm ${
                allCaughtUp ? 'text-[#0E2A47]' : 'text-[#C8641E]'
              }`}>
                <ListChecks size={18} className={allCaughtUp ? 'text-[#5FA718]' : 'text-[#C8641E]'} /> Required Actions
              </h2>
              <span
                data-testid="required-actions-count"
                className={`rounded-md px-2 py-0.5 text-xs font-bold text-white ${
                  allCaughtUp ? 'bg-[#5FA718]' : 'bg-[#C8641E]'
                }`}
              >{totalActions}</span>
            </div>
            {allCaughtUp ? (
              <p className="text-sm text-slate-700" data-testid="required-actions-caught-up">
                You're all caught up — no actions required right now.
              </p>
            ) : (
              <div className="space-y-2">
                {requiredActions.map((a) => (
                  a.emphasis ? (
                    <button key={a.key} onClick={a.onClick} data-testid={`required-action-${a.key}`}
                      className="flex w-full items-center justify-between rounded-xl border-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 px-4 py-4 text-left shadow-sm transition-colors">
                      <span className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white">
                          <BadgeDollarSign size={20} />
                        </span>
                        <span className="text-base font-semibold text-emerald-900">{a.label}</span>
                      </span>
                      <ChevronRight size={20} className="text-emerald-600" />
                    </button>
                  ) : (
                    <button key={a.key} onClick={a.onClick} data-testid={`required-action-${a.key}`}
                      className="flex w-full items-center justify-between rounded-lg bg-white/70 hover:bg-white px-4 py-3 text-left transition-colors">
                      <span className="text-sm font-medium text-slate-700">{a.label}</span>
                      <span className="flex items-center gap-2">
                        {!a.hideCount && <span className={`text-lg font-bold ${a.tone}`}>{a.count}</span>}
                        <ChevronRight size={16} className="text-slate-400" />
                      </span>
                    </button>
                  )
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Service Requests */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">Service Requests</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SrMetric testid="metric-open" label="Awaiting Client Approval" value={stats.new_requests} icon={Inbox} tone="text-[#C8641E]" onClick={() => onOpenRequests(SR_STATUS.SUBMITTED)} />
          <SrMetric testid="metric-emergency" label="Emergency" value={stats.emergency_requests} icon={AlertTriangle} tone="text-red-600" onClick={() => onOpenRequests(SR_STATUS.SUBMITTED)} />
          <SrMetric testid="metric-awaiting-wo" label="Awaiting WO" value={stats.awaiting_wo_processing} icon={ClipboardCheck} tone="text-green-700" onClick={() => onOpenRequests(SR_STATUS.APPROVED)} />
          <SrMetric testid="metric-info" label="Info Requested" value={stats.information_requested} icon={HelpCircle} tone="text-blue-600" onClick={() => onOpenRequests(SR_STATUS.INFO)} />
          <SrMetric testid="metric-hold" label="On Hold" value={stats.on_hold} icon={PauseCircle} tone="text-slate-600" onClick={() => onOpenRequests(SR_STATUS.HOLD)} />
          <SrMetric testid="metric-declined" label="Declined" value={stats.declined} icon={Clock3} tone="text-slate-600" onClick={() => onOpenRequests(SR_STATUS.DECLINED)} />
          <SrMetric testid="metric-total" label="Total Requests" value={stats.total_requests} icon={ListChecks} onClick={() => onOpenRequests()} />
        </div>
      </div>

      {/* Active Work Orders */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500">
            <Wrench size={15} /> Active Work Orders
          </h3>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="sla-legend">
            {SLA.map((s) => (
              <span key={s.p} className="rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                <span className="font-bold text-[#0E2A47]">{s.p}</span> · {s.t}
              </span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="active-wo-metrics">
          {WO_CARDS.map((c) => (
            <WoMetric key={c.key} testid={`wo-metric-${c.key}`} label={c.label} value={woMetrics[c.key]} tone={c.tone}
              dimmed={['scheduled', 'dispatched', 'in_progress', 'awaiting_authorization', 'awaiting_qc'].includes(c.key)}
              onClick={() => onOpenWorkOrders({ status: c.status })} />
          ))}
          <WoMetric testid="wo-metric-overdue" label="Overdue" value={woMetrics.overdue} tone="text-red-600"
            onClick={() => onOpenWorkOrders({ overdue: true })} />
        </div>
      </div>

      {/* Active Work */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-[#0E2A47]">Active Work</h3>
          <button onClick={() => onOpenWorkOrders({})} className="text-sm text-[#8FC93A] font-semibold hover:underline" data-testid="view-all-wos">View all work orders →</button>
        </div>
        <Card>
          <CardContent className="p-0">
            <Table data-testid="active-work-table">
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>WO #</TableHead>
                  <TableHead className="hidden md:table-cell">Customer / Location</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden lg:table-cell">Assigned</TableHead>
                  <TableHead className="hidden sm:table-cell">Required</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeList.slice(0, 25).map((w) => (
                  <TableRow key={w.id} className="cursor-pointer hover:bg-gray-50"
                    data-testid={`active-wo-row-${w.tfs_work_order_number}`} onClick={() => onSelectWorkOrder(w.id)}>
                    <TableCell className="font-mono text-xs font-semibold text-[#0E2A47]">{w.tfs_work_order_number}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-500">
                      <div className="truncate max-w-[200px]">{w.customer_name || '—'}</div>
                      <div className="text-xs text-slate-400 truncate max-w-[200px]">{w.service_location || ''}</div>
                    </TableCell>
                    <TableCell className="font-medium text-slate-800 max-w-[200px] truncate">{humanizeDates(w.job_name) || '—'}</TableCell>
                    <TableCell><span className="text-xs font-bold text-slate-600">{w.priority_level || '—'}</span></TableCell>
                    <TableCell><span className="text-xs">{w.status || '—'}</span></TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-slate-500">{w.assigned_to_name || 'Unassigned'}</TableCell>
                    <TableCell className="hidden sm:table-cell text-xs">
                      {isOverdue(w)
                        ? <span className="rounded-full bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 font-semibold">Overdue · {fmtDate(reqDate(w))}</span>
                        : <span className="text-slate-500">{fmtDate(reqDate(w))}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {activeList.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No active work orders.</div>}
          </CardContent>
        </Card>
      </div>

      {/* Recently Completed */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="flex items-center gap-2 text-lg font-bold text-[#0E2A47]"><CheckCircle2 size={18} className="text-green-600" /> Recently Completed</h3>
          <span className="rounded-full bg-green-100 text-green-700 px-2.5 py-0.5 text-xs font-semibold" data-testid="completed-today-count">{completedToday} today</span>
        </div>
        <Card>
          <CardContent className="p-0">
            {completedList.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm" data-testid="recently-completed-empty">
                Completed work orders will appear here. (QC & completion arrive in Phase 3.)
              </div>
            ) : (
              <Table data-testid="recently-completed-table">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>WO #</TableHead><TableHead>Job</TableHead>
                    <TableHead className="hidden md:table-cell">Customer</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {completedList.slice(0, 5).map((w) => (
                    <TableRow key={w.id} className="cursor-pointer hover:bg-gray-50"
                      data-testid={`completed-wo-row-${w.tfs_work_order_number}`} onClick={() => onSelectWorkOrder(w.id)}>
                      <TableCell className="font-mono text-xs font-semibold text-[#0E2A47]">{w.tfs_work_order_number}</TableCell>
                      <TableCell className="font-medium text-slate-800 max-w-[220px] truncate">{humanizeDates(w.job_name) || '—'}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-slate-500">{w.customer_name || '—'}</TableCell>
                      <TableCell><span className="text-xs">{w.status}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Service Requests quick list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-[#0E2A47]">New Service Requests</h3>
          <button onClick={() => onOpenRequests()} className="text-sm text-[#8FC93A] font-semibold hover:underline" data-testid="view-all-requests">View all →</button>
        </div>
        <ServiceRequestTable requests={newRequests} onSelect={onSelectRequest} emptyLabel="No new requests awaiting review." />
      </div>
    </div>
  );
};

export default OperationsDashboard;
